import React, { useEffect } from "react";
import { useState, createContext, useContext } from "react";
import axios from "axios";
import SessionExpiredModal from "../pages/modals/sessionExpiredmodal";
import ConflictIdentityErrorModal from "../pages/modals/conflictIdentityErrorModal";
import { v4 as uuidv4 } from "uuid";
import { featureLists, linkToFeatureMap } from "../../LinkToFeatureMap";
import { toast } from "react-toastify";
import { customToast } from "../reusableComponents/customToaster/customToast";
import { getCurrentBrowserFingerPrint } from "@rajesh896/broprint.js";
import FileSaver from "file-saver";
import { Navigate, useNavigate } from "react-router-dom";
import { isEmpty } from "../pages/utils/utils";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {

  const [merchantEmail, setMerchantEmail] = useState("");
  const [merchantLogo, setMerchantLogo] = useState();
  const [merchantName, setMerchantName] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem("accessToken"));
  const [timezone, setTimezone] = useState();
  const [approvalCount, setApprovalCount] = useState(0);
  const [isSideMenuContentLoading, setIsSideMenuContentLoading] = useState(true);

  const api = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
  });

  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [fingerPrintValue, setFingerPrintValue] = useState("");
  const [
    isConflictIdentityErrorModalOpen,
    setIsConflictIdentityErrorModalOpen,
  ] = useState(false);
  const [newConfigAdded, setNewConfigAdded] = useState(false);
  getCurrentBrowserFingerPrint().then((fingerprint) => {
    setFingerPrintValue(fingerprint);
  });
  const defaultPageLink = () => {
    if (getUserPermissions() === null) {
      return "/";
    }

    if (isAuthorizedFeature("ANALYTICS")) {
      return "/home";
    }


    for (let i = 0; i < getUserPermissions()?.length; i++) {
      let feature = getUserPermissions()[i]?.featureName;
      let link = linkToFeatureMap[feature];
      if (link && isAuthorizedFeature(feature)) {
        return link;
      }
    }
    return null;
  };

  const redirectToAccessDeniedPage = () => {
    window.location.href = "/accessDenied";
  };

  const checkPermission = (permission) => {
    if (getUserPermissions() !== null && !isAuthorizedPermission(permission)) {
      redirectToAccessDeniedPage();
    } else {
      return true;
    }
  };

  const showSessionModal = (err) => {
    if (window.location.pathname != "/") {
      setIsSessionModalOpen(true);
    } else {
      customToast(
        "error",
        "high",
        err.response.data.message
          ? err.response.data.message
          : "unexpected server error"
      )
    }
  };
  const showConflictIdentityErrorModal = () => {
    setIsConflictIdentityErrorModalOpen(true);
  };

  const getErrorMessage = (errResponse) => {
    if (errResponse?.data !== undefined) {
      if (errResponse?.data?.fieldErrorItems?.length > 0) {
        return errResponse?.data?.fieldErrorItems
          .map((errItem) => `${errItem?.message}`)
          .join("\n");
      }
      return errResponse?.data?.message;
    }
    return errResponse?.statusText;
  };

  const handleApiError = (
    err,
    error,
    showConflictIdentityModal = false,
    otpError = false,
    sessionModal = true
  ) => {
    if (err?.response && err?.response?.status === 401 && sessionModal) {
      showSessionModal(err);
    } else if (err?.response && err?.response?.status === 403) {
      redirectToAccessDeniedPage();
    } else if (
      err?.response &&
      err?.response?.status === 409 &&
      showConflictIdentityModal
    ) {
      showConflictIdentityErrorModal();
    } else if (error != undefined) {
      if (err?.response) {
        const errorMsg = getErrorMessage(err?.response);

        error(errorMsg, err?.response?.status);
      } else {
        error(err?.message, err?.response?.status);
      }
    }
  };

  //Login Authentication
  const login = async (email, password, success, error) => {
    const signInData = {
      email: email,
      password: password,
    };

    try {
      const res = await api.post("/auth/users/auth", signInData, {
        headers: {
          Authorization: getAccessToken(),
          "X-Device-Fingerprint": fingerPrintValue,
        },
      });
      if (res?.data?.user?.requiresPwdChange || !res?.data?.token) {
        success(res?.data);
      } else {
        localStorage.setItem("accessToken", res?.data?.token);
        localStorage.setItem("userDetails", JSON.stringify(res?.data?.user));
        localStorage.setItem(
          "userPermissions",
          JSON.stringify(res?.data?.permissions)
        );
        if (success != undefined && localStorage.getItem("userPermissions")) {
          setIsLoggedIn(true);
          success(res?.data);
        }
      }
    } catch (err) {
      error(err);
    }
  };

  const twoFAAuth = async (payload, success, error) => {
    try {
      const res = await api.post(
        `/auth/users/two-factor-auth`,
        {
          ...payload,
        },
        {
          headers: {
            Authorization: getAccessToken(),
            "X-Device-Fingerprint": fingerPrintValue,
          },
        }
      );
      localStorage.setItem("accessToken", res?.data?.token);
      localStorage.setItem("userDetails", JSON.stringify(res?.data?.user));
      localStorage.setItem(
        "userPermissions",
        JSON.stringify(res?.data?.permissions)
      );
      if (success != undefined && localStorage.getItem("userPermissions")) {
        setIsLoggedIn(true);
        success(res?.data);
      }
    } catch (err) {
      error(err?.response?.data?.message);
    }
  };

  //Session Token Authentication
  const validateToken = async (merchantId, token, success, error) => {
    const signInData = {
      merchantId: merchantId,
      token: token,
    };

    await api
      .post("/auth/session-tokens/auth", signInData)
      .then((res) => {
        localStorage.setItem("accessToken", res?.data?.token);
        localStorage.setItem(
          "userPermissions",
          JSON.stringify(res?.data?.permissions)
        );

        getUserProfile(
          merchantId,
          res?.data?.actorId,
          (userData) => {
            localStorage.setItem("userDetails", JSON.stringify(userData));

            if (success != undefined) {
              setIsLoggedIn(true);
              success(res?.data);
            }
          },
          (err) => handleApiError(err, error)
        );
      })
      .catch((err) => handleApiError(err, error));
  };

  const initiateMasqueradeSamlLogin = async (masqueradeEmail) => {
    window.location.href = `${process.env.REACT_APP_BASE_URL}/auth/masquerade/${masqueradeEmail}/initiate/saml`;
  };

  const getUserDetails = () => {
    if (
      window.location.pathname != "/" &&
      window.location.pathname != "/accessDenied" &&
      !window.location.pathname.includes("/token")
    ) {
      localStorage.setItem(
        "currentPathname",
        window.location.pathname + window.location.search
      );
    }
    return JSON.parse(localStorage.getItem("userDetails"));
  };

  const getUserPermissions = () => {
    return localStorage.getItem("userPermissions") == undefined
      ? undefined
      : JSON.parse(localStorage.getItem("userPermissions"));
  };

  const isAuthorizedFeature = (feature) => {
    return (
      getUserPermissions()?.find(
        (userPermission) => userPermission.featureName === feature
      ) !== undefined
    );
  };

  const isAuthorizedPermission = (permission) => {
    return (
      getUserPermissions()?.find(
        (userPermission) => userPermission.name === permission
      ) !== undefined
    );
  };

  const getAccessToken = () => {
    return `Session ${localStorage.getItem("accessToken")}`;
  };

  const merchantSettings = async (success, error) => {
    const merchantId = getUserDetails()?.merchantId;
    const cachedData = localStorage.getItem("merchantSettings");

    if (cachedData) {
      if (success !== undefined) {
        const settings = JSON.parse(cachedData);
        success(settings);
        setMerchantLogo(settings?.logoUrl);
        setMerchantName(settings?.merchantName);
        setTimezone(settings?.timeZone);
      }
    } else {
      await api
        .get(`/merchants/${merchantId}/settings`, {
          headers: {
            Authorization: getAccessToken(),
          },
        })
        .then((res) => {
          if (res.data.logoUrl) {
            res.data.logoUrl = res.data.logoUrl + `?token=${uuidv4()}`;
          }
          localStorage.setItem("merchantSettings", JSON.stringify(res.data));
          if (success != undefined) {
            success(res?.data);
            setTimezone(res?.data?.timeZone);
            setMerchantLogo(res.data?.logoUrl);
            setMerchantName(res?.data?.merchantName);
          }
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  //SignUp function
  const signUp = async (name, companyName, email, mobile, success, error) => {
    const merchantEnteredData = {
      name: name,
      companyName: companyName,
      email: email,
      mobileNo: mobile,
    };

    await api
      .post("/merchants", merchantEnteredData)
      .then((res) => {
        setMerchantEmail(res.data.email);
        if (success != undefined) {
          setIsLoggedIn(true);
          success(res.data);
        }
      })
      .catch((err) => {
        err = err.response.data;

        error(err.message);
      });
  };

  const getPspConfigSchemas = async (pspCodes, success, error) => {
    pspCodes.forEach(async (pspCode) => {
      const cachedData = localStorage.getItem(pspCode + "_schema");

      if (cachedData) {
        success(pspCode, JSON.parse(cachedData));
      } else {
        await api
          .get(`/platform/psp-configs/${pspCode}/schema`)
          .then((response) => {
            localStorage.setItem(
              pspCode + "_schema",
              JSON.stringify(response.data)
            );
            success(pspCode, response.data);
          })
          .catch((err) => {
            error(pspCode, err.message);
          });
      }
    });
  };

  //PSP Configuration function
  const savePspConfigs = async (
    legalEntity,
    pspCode,
    countryCode,
    pspConfig,
    success,
    error
  ) => {
    checkPermission("ADD_PAYMENT_GATEWAY");

    const merchantId = getUserDetails()?.merchantId;

    await api
      .post(
        `/merchants/${merchantId}/legal-entities/${legalEntity}/psp-configs/${pspCode}/countries/${countryCode}`,
        pspConfig,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      )
      .then((res) => {
        localStorage.removeItem("allModalData");
        if (success != undefined) {
          success(res.data);
          setNewConfigAdded(true);
        }
      })
      .catch((err) => {
        err = err.response.data;
        error(
          err.fieldErrorItems === undefined || err.fieldErrorItems.length === 0
            ? err.message
            : err.fieldErrorItems.map((errItem) => errItem.message).join(", ")
        );
      });
  };

  const delPspConfigu = async (pspCode, merchantId, success, error) => {
    await api
      .delete(`/merchants/${merchantId}/psp-configs/${pspCode}`, {
        headers: {
          Authorization: getAccessToken(),
        },
      })
      .then((res) => {
        if (success != undefined) {
          success(res.data);
        }
      })
      .catch((err) => handleApiError(err, error));
  };

  //Routing Rules
  const getRoutes = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;

    await api
      .get(`/merchants/${merchantID}/routing-rules`, {
        headers: {
          Authorization: getAccessToken(),
        },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  //PSP Configs
  const getPspConfig = async (success, error) => {
    checkPermission("VIEW_GATEWAY_CONFIGURATIONS")

    const merchantID = getUserDetails()?.merchantId;

    await api
      .get(`/merchants/${merchantID}/psp-configs`, {
        headers: {
          Authorization: getAccessToken(),
        },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  //Order Details
  const getOrderDetails = async (transactionId, success, error, operationPermissions) => {
    const userDetails = getUserDetails();
    const merchantID = userDetails?.merchantId;
    const email = userDetails?.email;

    let url = `/merchants/${merchantID}/transactions/${transactionId}`;
    if (isAuthorizedPermission(operationPermissions?.viewOwnOrderDetails)
      && !isAuthorizedPermission(operationPermissions?.viewOrderDetails)) {
      url = `/merchants/${merchantID}/users/${email}/transactions/${transactionId}`;
    }

    await api.get(url, {
      headers: { Authorization: getAccessToken() },
    })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => {
        if (!transactionId) {
          error("noTransactionId");
        } else if (err?.response?.status === 404) {
          error("invalidTransactionId");
        } else {
          handleApiError(err, error);
        }
      });
  };

  const getOrderDetailsTimeline = async (transactionId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .get(`/merchants/${merchantID}/transactions/${transactionId}/timeline`, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => {
        if (!transactionId) {
          error("noTransactionId");
        } else if (err?.response?.status === 404) {
          error("invalidTransactionId");
        } else {
          handleApiError(err, error);
        }
      });
  };

  //Business Units API
  const getBusinessUnits = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .get(`/merchants/${merchantID}/legal-entities`, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  //All PSP Codes
  const getAllPspConfigs = async (success, error) => {
    const cachedData = localStorage.getItem("platformPspConfigs");

    if (cachedData) {
      success !== undefined && success(JSON.parse(cachedData));
    } else {
      await api
        .get("/platform/psp-configs")
        .then((res) => {
          localStorage.setItem("platformPspConfigs", JSON.stringify(res.data));
          success !== undefined && success(res.data);
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  const getAllPaymentMethods = async (success, error) => {
    const cachedData = localStorage.getItem("platformPaymentMethods");

    if (cachedData) {
      success !== undefined && success(JSON.parse(cachedData));
    } else {
      await api
        .get("/platform/payment-methods")
        .then((res) => {
          localStorage.setItem(
            "platformPaymentMethods",
            JSON.stringify(res.data)
          );
          success !== undefined && success(res.data);
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  const getAllModalData = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    const cachedData = localStorage.getItem("allModalData");
    if (cachedData) {
      success(JSON.parse(cachedData));
    } else {
      await api
        .post(`/merchants/${merchantID}/config-suggesters`, undefined, {
          headers: { Authorization: getAccessToken() },
        })
        .then((res) => {
          localStorage.setItem("allModalData", JSON.stringify(res.data));
          success(res.data);
        })
        .catch((err) => handleApiError(err, error));
    }
  };


  const getAllEmiOffers = async (currency, minAmount, maxAmount, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    let url = `/merchants/${merchantID}/payment-methods/search`;
    let payload = {
      currency,
      minAmount,
      maxAmount,
      applicableOffers: true
    };
    try {
      const res = await api.post(url, payload, {
        headers: { Authorization: getAccessToken() },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getEmiOfferById = async (id, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    let url = `/merchants/${merchantID}/offers/${id}`;
    try {
      const res = await api.get(url, {
        headers: { Authorization: getAccessToken() },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const deleteEmiOfferById = async (id, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    let url = `/merchants/${merchantID}/offers/${id}`;
    try {
      const res = await api.delete(url, {
        headers: { Authorization: getAccessToken() },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const addEmiRule = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(`merchants/${merchantID}/offers`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const updateEmiRule = async (payload, id, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(`merchants/${merchantID}/offers/${id}`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getPaymentMethod = async (success, error) => {
    const cachedData = localStorage.getItem("paymentMethods");

    if (cachedData) {
      success(JSON.parse(cachedData));
    } else {
      await api
        .get("/platform/payment-methods")
        .then((response) => {
          localStorage.setItem("paymentMethods", JSON.stringify(response.data));
          success(response.data);
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  const updatePaymentMethod = async (
    pspName,
    legal_entity,
    payload,
    success,
    error
  ) => {
    const merchantId = getUserDetails()?.merchantId;
    try {
      const res = await api.put(
        `/merchants/${merchantId}/legal-entities/${legal_entity}/psp-configs/${pspName}`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res?.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  // Calling API's for All / Refund/ Void-Capture
  const mainAPICall = async (payload, success, error, operationPermissions) => {
    const userDetails = getUserDetails();
    const merchantID = userDetails?.merchantId;
    const email = userDetails?.email;

    let url = `/merchants/${merchantID}/visualizations/entities/payment_operations`;
    if (isAuthorizedPermission(operationPermissions?.viewOwnOrder)
      && !isAuthorizedPermission(operationPermissions?.viewOrder)) {
      url = `/merchants/${merchantID}/visualizations/users/${email}/entities/payment_operations`;
    }

    await api
      .post(url, manageCurrencyInPayload(payload), {
        headers: { Authorization: getAccessToken() },
      }
      )
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  const getAllTransactionDetails = async (payload, success, error, operationPermissions) => {
    await mainAPICall(payload, success, error, operationPermissions);
  };

  const getRefundData = async (payload, success, error) => {
    await mainAPICall(payload, success, error);
  };

  const getVoidCaptureData = async (payload, success, error) => {
    await mainAPICall(payload, success, error);
  };

  const getApiCredentials = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .get(`/merchants/${merchantID}/api-keys`, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  const getApiCredentialsName = async (name, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(`/merchants/${merchantID}/api-keys?name=${name}`, payload, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  const getAllUiAnalyticsEvent = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get("/platform/ui-analytics-events", {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const manageCurrencyInPayload = (payload) => {
    const defaultCurrency = process.env.REACT_APP_DEFAULT_CURRENCY;
    if (payload.attributeFilters !== undefined && defaultCurrency !== "false") {
      if (payload.attributeFilters.currencyCode !== undefined) {
        payload.attributeFilters.currencyCode =
          payload.attributeFilters.currencyCode;
      } else {
        payload.attributeFilters.currencyCode = defaultCurrency;
      }
    }
    return payload;
  };

  const getAllVisualizationData = async (
    visualization,
    payload,
    success,
    error,
    operationPermissions
  ) => {
    const userDetails = getUserDetails();
    const merchantID = userDetails?.merchantId;
    const email = userDetails?.email;

    let url = `/merchants/${merchantID}/visualizations/${visualization}`;
    if (
      visualization === "payment_links" &&
      !isAuthorizedPermission(operationPermissions?.viewAllLinks) &&
      isAuthorizedPermission(operationPermissions?.viewOwnLinks)
    ) {
      url = `/merchants/${merchantID}/visualizations/users/${email}/${visualization}`;
    }

    try {
      const res = await api.post(url, manageCurrencyInPayload(payload), {
        headers: {
          Authorization: getAccessToken(),
        },
      });

      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getAllVisualizationFilterData = async (
    visualization,
    payload,
    success,
    error
  ) => {
    const userDetails = getUserDetails();
    const merchantID = userDetails?.merchantId;
    let url = `/merchants/${merchantID}/visualizations/entities/filters/${visualization}`;
    try {
      const res = await api.post(url, payload, {
        headers: {
          Authorization: getAccessToken(),
        },
      });

      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const uploadPaymentFormData = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;

    await api
      .post(
        `/merchants/${merchantID}/payment-links`,
        manageCurrencyInPayload(payload),
        {
          headers: { Authorization: getAccessToken() },
        }
      )
      .then((res) => {
        success(res.data);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };

  const getQrDetails = async ({ downloadQrToken, success, error }) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(
        `/merchants/${merchantID}/payment-links/${downloadQrToken}/tracking-urls`,
        {
          headers: { Authorization: getAccessToken() },
        }
      );
      success(res?.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const credentialsCheck = async (payload, success, error, twoFa) => {
    let url = twoFa
      ? `/auth/users/two-factor-auth/resend?email=${payload?.email}`
      : `/auth/users/reset-pwd/initiate?email=${payload?.email}`;
    await api
      .post(url)
      .then((res) => {
        success(res?.data);
      })
      .catch((err) => {
        console.log("carch::", { err, error });

        handleApiError(err, error);
      });
  };

  const verifyOTP = async (payload, success, error) => {
    await api
      .post(`/auth/users/reset-pwd/validate-otp`, payload)
      .then((res) => {
        success(res.data);
      })
      .catch((err) => {
        if (err?.response) {
          const errorMsg = getErrorMessage(err?.response);
          error(errorMsg);
        } else {
          error(err?.message);
        }
      });
  };

  const updatePassword = async (payload, success, error) => {
    await api
      .post(`/auth/users/reset-pwd`, payload)
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  const getUserProfile = async (merchantId, email, success, error) => {
    const cachedData = localStorage.getItem("merchantProfiles");

    if (cachedData) {
      if (success !== undefined) {
        success(JSON.parse(cachedData));
      }
    } else {
      await api
        .get(`merchants/${merchantId}/users/${email}/profiles`, {
          headers: {
            Authorization: getAccessToken(),
          },
        })
        .then((res) => {
          localStorage.setItem("merchantProfiles", JSON.stringify(res.data));
          if (success != undefined) {
            success(res.data);
          }
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  const merchantProfiles = async (success, error) => {
    const merchantId = getUserDetails()?.merchantId;
    const email = getUserDetails()?.email;

    getUserProfile(merchantId, email, success, error);
  };

  const updateMerchantProfiles = async (payload, success, error) => {
    const merchantId = getUserDetails()?.merchantId;
    const email = getUserDetails()?.email;
    try {
      const res = await api.put(
        `merchants/${merchantId}/users/${email}/profiles`,
        payload,
        {
          headers: { Authorization: getAccessToken() },
        }
      );
      localStorage.setItem("merchantProfiles", JSON.stringify(res.data));
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const updateCheckoutSettings = async (payload, success, error) => {
    checkPermission("UPDATE_MERCHANT_SETTINGS");

    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(`merchants/${merchantID}/settings`, payload, {
        headers: { Authorization: getAccessToken() },
      });

      localStorage.setItem("merchantSettings", JSON.stringify(res.data));
      success(res.data);
    } catch (err) {
      handleApiError(err, error, true);
    }
  };

  const changePassword = async (email, payload, success, error) => {
    const user_email = getUserDetails()?.email || email;

    await api
      .post(`/auth/users/${user_email}/change-pwd`, payload)
      .then((res) => {
        success(res.data);
      })
      .catch((err) => {
        handleApiError(err, error, false, false, false);
      });
  };

  const getDownloadFile = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .get(
        `/merchants/${merchantID}/visualizations/entities/payment_operations/download?fromDate=${payload.fromDate}&toDate=${payload.toDate}`,
        {
          headers: { Authorization: getAccessToken() },
        }
      )
      .then((res) => {
        success(res);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };

  const getDownloadFileWithFilters = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(
        `/merchants/${merchantID}/visualizations/entities/payment_operations/download`,
        payload,
        { headers: { Authorization: getAccessToken() } }
      )
      .then((res) => {
        success(res);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };

  const getDownloadPSPReport = (url, success, error) => {
    api
      .get(url, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };

  const generateReports = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(`/merchants/${merchantID}/reports`, payload, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error, false));
  };

  const generateGenericReconReports = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(`/merchants/${merchantID}/generic-recon-reports`, payload, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error, false));
  };

  const getAllReportType = async (success, error) => {
    const cachedData = localStorage.getItem("reportType");
    if (cachedData) {
      if (success !== undefined) {
        success(JSON.parse(cachedData));
      }
    } else {
      await api
        .get(`/platform/reports/metadata`, {
          headers: {
            Authorization: getAccessToken(),
          },
        })
        .then((res) => {
          localStorage.setItem("reportType", JSON.stringify(res.data));
          if (success != undefined) {
            success(res.data);
          }
        })
        .catch((err) => handleApiError(err, error));
    }
  };
  const getAllReportStatus = async (success, error) => {
    const cachedData = localStorage.getItem("statusType");
    if (cachedData) {
      if (success !== undefined) {
        success(JSON.parse(cachedData));
      }
    } else {
      await api
        .get(`/platform/reports/metadata`, {
          headers: {
            Authorization: getAccessToken(),
          },
        })
        .then((res) => {
          localStorage.setItem("statusType", JSON.stringify(res.data));
          if (success != undefined) {
            success(res.data);
          }
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  const captureRefundPayment = async (
    transactionId,
    value,
    payload,
    success,
    error,
    id
  ) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `merchants/${merchantID}/transactions/${transactionId}/${value}`,
        payload,
        {
          headers: { Authorization: getAccessToken(), "X-Request-Id": id },
        }
      );
      success(res.data);
    } catch (err) {
      error(err);
    }
  };

  const getRefundReasons = async (success, error) => {
    const cachedData = localStorage.getItem("refundReasons");
    if (cachedData) {
      success(JSON.parse(cachedData));
    } else {
      await api
        .get("/platform/refund-reasons")
        .then((response) => {
          localStorage.setItem("refundReasons", JSON.stringify(response.data));
          success(response.data);
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  const getProductDetails = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .get(`/merchants/${merchantID}/products`, {
        headers: { Authorization: getAccessToken() },
      })
      .then((res) => {
        success(res.data);
      })
      .catch((err) => handleApiError(err, error));
  };

  const getCountryNames = async (success, error) => {
    const cachedData = localStorage.getItem("countryNames");

    if (cachedData) {
      if (success !== undefined) {
        success(JSON.parse(cachedData));
      }
    } else {
      await api
        .get(`/platform/countries`, {
          headers: {
            Authorization: getAccessToken(),
          },
        })
        .then((res) => {
          localStorage.setItem("countryNames", JSON.stringify(res.data));
          if (success != undefined) {
            success(res.data);
          }
        })
        .catch((err) => handleApiError(err, error));
    }
  };

  const postPspConfigForFiltering = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `merchants/${merchantID}/config-suggesters`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const addRoutingRule = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `merchants/${merchantID}/routing-rules`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getRoutingRules = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(`merchants/${merchantID}/routing-rules`, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getRoutingRuleById = async (ruleId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(
        `merchants/${merchantID}/routing-rules/${ruleId}`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const editRoutingRules = async (ruleId, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(
        `merchants/${merchantID}/routing-rules/${ruleId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const deleteRoutingRules = async (id, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.delete(
        `merchants/${merchantID}/routing-rules/${id}`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const addBusinessUnit = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/legal-entities`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const updateBusinessUnit = async (legalEntity, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(
        `/merchants/${merchantID}/legal-entities/${legalEntity}`,

        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getAllWebhookDetails = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(
        `/merchants/${merchantID}/vayu/settings/StatusNotification`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getSaltKey = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    const cachedData = localStorage.getItem(`saltKey_${merchantID}`);

    if (cachedData) {
      success && success(JSON.parse(cachedData));
    } else {
      try {
        const res = await api.get(`/merchants/${merchantID}/setup-configs`, {
          headers: {
            Authorization: getAccessToken(),
          },
        });
        localStorage.setItem(`saltKey_${merchantID}`, JSON.stringify(res.data));
        success && success(res.data);
      } catch (err) {
        handleApiError(err, error);
      }
    }
  };

  const getSpecificIdWebhookDetails = async (webhookId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(
        `/merchants/${merchantID}/vayu/settings/StatusNotification/${webhookId}`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );

      success(res.data);
    } catch (err) {
      if (!webhookId) {
        error("noWebhookId");
      } else if (
        err?.response?.status === 400 ||
        err?.response?.status === 404
      ) {
        error("invalidWebhookId");
      } else {
        handleApiError(err, error);
      }
    }
  };

  const postNotificationStatus = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/visualizations/status_notification_report`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const createWebhook = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/vayu/settings`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const updateWebhoookDetails = async (webhookId, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(
        `/merchants/${merchantID}/vayu/settings/StatusNotification/${webhookId}`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const deleteWebhookDetails = async (webhookId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.delete(
        `/merchants/${merchantID}/vayu/settings/StatusNotification/${webhookId}`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const addProduct = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(`/merchants/${merchantID}/products`, payload, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const updateProduct = async (productCode, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(
        `/merchants/${merchantID}/products/${productCode}`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const deleteProduct = async (productCode, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.delete(
        `/merchants/${merchantID}/products/${productCode}`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getReconSampleFile = async (processor, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const response = await api.get(
        `/merchants/${merchantID}/reconciliations/psps/${processor}/sample-files`,
        {
          responseType: "blob",
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      const contentDisposition = response.headers["content-disposition"];

      if (!contentDisposition) {
        error("Content-Disposition header not found in the response.");
        return;
      }
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);

      if (!filenameMatch || !filenameMatch[1]) {
        error("Filename not found in Content-Disposition header.");
        return;
      }

      const filename = filenameMatch[1];
      const link = document.createElement("a");
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      success(response);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getReconConflictFile = async (fileId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const response = await api.get(
        `/merchants/${merchantID}/reconciliations/conflict-files/${fileId}`,
        {
          responseType: "blob",
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      const contentDisposition = response.headers["content-disposition"];

      if (!contentDisposition) {
        error("Content-Disposition header not found in the response.");
        return;
      }
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);

      if (!filenameMatch || !filenameMatch[1]) {
        error("Filename not found in Content-Disposition header.");
        return;
      }

      const filename = filenameMatch[1];
      const link = document.createElement("a");
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      success(response);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const postReconFile = async (processor, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/reconciliations/psps/${processor}`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      error(err);
    }
  };

  const getSupportedPspRecon = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    const cachedData = localStorage.getItem(`pspRecon`);
    const allowed = isAuthorizedPermission("VIEW_RECON_REPORTS");

    if (allowed) {
      if (cachedData && !newConfigAdded) {
        success && success(JSON.parse(cachedData));
      } else {
        try {
          const res = await api.get(
            `/merchants/${merchantID}/reconciliations/supported-psps`,
            {
              headers: {
                Authorization: getAccessToken(),
              },
            }
          );
          localStorage.setItem(`pspRecon`, JSON.stringify(res.data));
          success && success(res.data);
        } catch (err) {
          error(err);
        }
        if (newConfigAdded) {
          setNewConfigAdded(false);
        }
      }
    }
  };

  const isSupportedRecon = async () => {
    return new Promise((resolve, reject) => {
      getSupportedPspRecon(
        (data) => {
          if (Array.isArray(data.pspCodes) && data.pspCodes.length === 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        },
        (errMsg) => {
          reject(errMsg);
        }
      );
    });
  };

  const postSettlementFiles = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/reconciliations/files`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getAllUsers = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(`/merchants/${merchantID}/users`, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const createUsers = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(`/merchants/${merchantID}/users`, payload, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error, false);
    }
  };
  const getAllRoles = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(`/merchants/${merchantID}/roles`, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const getAllPermissions = async (success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(`/merchants/${merchantID}/permissions`, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const createRole = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(`/merchants/${merchantID}/roles`, payload, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error, false);
    }
  };

  const updateUser = async (emailId, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(`/merchants/${merchantID}/users`, payload, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error, false);
    }
  };
  const updateRole = async (id, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(
        `/merchants/${merchantID}/roles/${id}`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error, false);
    }
  };

  const getRoleById = async (id, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.get(`/merchants/${merchantID}/roles/${id}`, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const deleteUserById = async (emailId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.delete(
        `/merchants/${merchantID}/users?email=${emailId}`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const deleteRoleById = async (id, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.delete(`/merchants/${merchantID}/roles/${id}`, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const resendInviteForUser = async (emailId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/invite-user?email=${emailId}`,
        {},
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const updateLogo = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(`/merchants/${merchantID}/logos`, payload, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res?.data);
      localStorage.removeItem("merchantSettings");
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const supportedMethods = async (
    legalEntity,
    pspCode,
    payload,
    success,
    error
  ) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/legal-entities/${legalEntity}/psp-configs/${pspCode}/payment-methods`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      error(err.response.data.errorCode);
      if (err.response.data.errorCode == "unauthorized") {
        handleApiError(err, error);
      } else {
        customToast(
          "error",
          "high",
          err.response.data.message
            ? err.response.data.message
            : "unexpected server error"
        );
      }
    }
  };

  const resendSMSAndEmail = async (token, type, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/payment-links/${token}/resend/${type}`,
        "",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const editPaymentLink = async (token, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.put(
        `/merchants/${merchantID}/payment-links/${token}`,
        manageCurrencyInPayload(payload),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const expirePaymentLink = async (token, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/payment-links/${token}/expire`,
        token,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  // get all offers
  const getAllOffer = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const response = await api.post(
        `/merchants/${merchantID}/visualizations/entities/merchant_offers`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const getAllSetupConfigs = async (selectedEnv, success, error) => {
    try {
      const response = await api.get(`/domains/${selectedEnv}/setup-configs`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  //Logout function
  const logout = () => {
    sessionStorage.clear();
    localStorage.clear();

    setIsLoggedIn(false);


  };


  // OLD GENERIC RECON END POINTS
  const getGenericReconVisualizationData = async (
    visualization,
    payload,
    success,
    error,
    operationPermissions
  ) => {
    const userDetails = getUserDetails();
    const merchantID = userDetails?.merchantId;

    let url = `/merchants/${merchantID}/generic-reconciliations/visualizations/${visualization}`;
    try {
      const res = await api.post(url, manageCurrencyInPayload(payload), {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const getGenericReconConfig = async (success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;

      const response = await api.get(
        `/merchants/${merchantId}/generic-reconciliations/recon-configs`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const getGenericReconTableData = async (success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;

      const response = await api.get(
        `/merchants/${merchantId}/generic-reconciliations/recon-record-files`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const getGenericReconRecordFiles = async (success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;

      const response = await api.get(
        `/merchants/${merchantId}/generic-reconciliations/recon-file-set-configs`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const postGenericReconFile = async (fileSetCode, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.post(
        `/merchants/${merchantID}/generic-reconciliations/file-sets/${fileSetCode}/recon-record-files`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(res.data);
    } catch (err) {
      error(err);
    }
  };
  const getReconTags = async (success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;
      const response = await api.get(
        `/merchants/${merchantId}/generic-reconciliations/tags`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const revertReconFile = async (fileName, reconId, payload, success, error) => {
    try {
      const merchantID = getUserDetails()?.merchantId;
      const response = await api.post(
        `merchants/${merchantID}/generic-reconciliations/reconciliations/${reconId}/recon-record-files/${fileName}/revert`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  // OLD GENERIC RECON END POINTS

  // NEW GENERIC RECON END POINTS
  const getGenericReconciliationConfig = async (success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;
      const response = await api.get(`/merchants/${merchantId}/generic-reconciliations/reconciliations/configs`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      localStorage.setItem("recon-config", JSON.stringify(response.data));
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const getGenericReconciliationVisualizationData = async (
    visualization,
    reconId,
    payload,
    success,
    error,
    operationPermissions
  ) => {
    const userDetails = getUserDetails();
    const merchantID = userDetails?.merchantId;

    let url = `/merchants/${merchantID}/generic-reconciliations/reconciliations/${reconId}/visualizations/${visualization}`;
    try {
      const res = await api.post(url, manageCurrencyInPayload(payload), {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const revertReconciliationFile = async (fileName, reconId, success, error) => {
    try {
      const merchantID = getUserDetails()?.merchantId;
      const response = await api.post(
        `merchants/${merchantID}/generic-reconciliations/reconciliations/${reconId}/recon-record-files/${fileName}/revert`,
        {},
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response);
    } catch (err) {
      handleApiError(err, error);
    }
  }
  const getGenericReconciliationRecordFiles = async (reconId, success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;
      const response = await api.get(
        `/merchants/${merchantId}/generic-reconciliations/reconciliations/${reconId}/file-sets/configs`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  const getReconciliationTags = async (reconId, success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;
      const response = await api.get(
        `/merchants/${merchantId}/generic-reconciliations/reconciliations/${reconId}/tags`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };
  // NEW GENERIC RECON END POINTS


  const downloadInvalidReconFile = (filename) => {
    const merchantID = getUserDetails()?.merchantId;
    return `${process.env.REACT_APP_BASE_URL}/merchants/${merchantID}/generic-reconciliations/${filename}/invalid-record-files`;
  };
  const downloadFileUrlOpen = async (url) => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        showSessionModal();
      }
      const downloadUrl = `${url}?stt=${accessToken}`;
      window.open(downloadUrl, "_blank");
    } catch (err) {
      customToast(
        "error",
        "high",
        "Unexpected error occurred. Try again later."
      );
    }
  };

  const globalFileDownload = async (url, success, error) => {
    try {
      const response = await api.get(url, {
        headers: { Authorization: getAccessToken() },
      });
      if (response) {
        if (!response.headers) {
          return;
        }

        const contentDisposition = response.headers["content-disposition"];
        const contentType = response.headers["content-type"];
        const match = contentDisposition?.match(/filename="(.+?)"/);
        let filename;

        if (match && match[1]) {
          filename = match[1];
        } else {
          filename = "Data.csv";
        }

        if (!response.data) {
          throw new Error("Response data is missing.");
        }

        const blob = new Blob([response.data], {
          type: contentType || "application/octet-stream",
        });

        if (
          typeof FileSaver === "undefined" ||
          typeof FileSaver.saveAs !== "function"
        ) {
          return console.warn("File saver is not defined");
        }

        FileSaver.saveAs(blob, filename);
        if (success && typeof success === "function") {
          success(response);
        }
      }
    } catch (err) {
      if (error) {
        handleApiError(err, error);
      }
    }
  };

  const getFileSetCodesByTags = async (payload, success, error) => {
    try {
      const merchantID = getUserDetails()?.merchantId;
      const response = await api.post(
        `merchants/${merchantID}/generic-reconciliations/fileSets`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response?.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const refreshOrderDetail = async (transactionId, success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;
      const response = await api.get(
        `/merchants/${merchantId}/transactions/${transactionId}/transaction-sync`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const getProductDetailsById = async (productId, success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;
      const response = await api.get(
        `/merchants/${merchantId}/products/${productId}`,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const globalFileUpload = async (url, payload, success, error, onProgress, abortController) => {
    try {
      const res = await api.post(url, payload, {
        headers: {
          Authorization: getAccessToken(),
        },
        onUploadProgress: onProgress,
        signal: abortController.signal,
      });
      success(res.data);
    } catch (err) {
      if (err.name === "AbortError") {
      } else {
        error(err);
      }
    }
  };

  const getDynamicRoute = (route) => {
    // this checks if the route is dynamic or static
    // if static then return as it is
    // in case of dynamic it tries to fetch the id from storage at some interval
    return new Promise((res, rej) => {
      const dynamicRouteRegex = /\/:(\w+)/;
      const match = route?.match(dynamicRouteRegex);
      if (!match) res(route)
      const id = match[1];
      let idValue = localStorage.getItem(id); // Get the value from localStorage
      let intervalId = setInterval(() => {
        if (isEmpty(idValue)) {
          idValue = localStorage.getItem(id);
        } else {
          clearInterval(intervalId);
          res(route.replace(`:${id}`, idValue))
        }
      }, 100)
    })
  }

  const handleNavigation = async () => {
    return new Promise(async (resolve) => {
      if (isEmpty(featureLists)) resolve("/home");
      const location = window.location;
      const searchParams = new URLSearchParams(location?.search);
      const redirectUrl = searchParams.get("redirectUrl");
      searchParams.delete("redirectUrl");
      const remainingParams = searchParams.toString();

      const isRedirectUrlValid = () => {
        if (!redirectUrl) return false;
        const userPermissions = localStorage.getItem("userPermissions")
          ? JSON.parse(localStorage.getItem("userPermissions"))
          : undefined;

        if (!userPermissions) return false;

        let featureName = "";
        featureLists.some((obj) => {
          if (obj.path === `/${redirectUrl}`) {
            featureName = obj.feature;
            return true;
          }
        });
        return isAuthorizedFeature(featureName);
      };
      const landingPage = isRedirectUrlValid() ? `${redirectUrl}${remainingParams ? `?${remainingParams}` : ""}` : defaultPageLink();
      const finalUrl = landingPage;
      const url = await getDynamicRoute(finalUrl);
      resolve(url || "/home");
    });
  }

  const NavigateToAvailableRoute = () => {
    const navigate = useNavigate();
    useEffect(() => {
      const fetchRoute = async () => {
        const route = await handleNavigation();
        if (route) {
          navigate(route);
        }
      };
      fetchRoute();
    }, [navigate]);
    return null
  };

  const routingRuleSuggester = async (payload, success, error) => {
    try {
      const merchantID = getUserDetails()?.merchantId;
      const response = await api.post(
        `merchants/${merchantID}/routing-rules/suggesters`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response);
      return response?.data
    } catch (err) {
      handleApiError(err, error);
    }
  }
  const addRoutingRules = async (payload, success, error) => {
    try {
      const merchantID = getUserDetails()?.merchantId;
      const response = await api.post(
        `merchants/${merchantID}/routing-rules`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response);
    } catch (err) {
      handleApiError(err, error);
    }
  }
  const updateRoutingRule = async (id, payload, success, error) => {
    try {
      const merchantID = getUserDetails()?.merchantId;
      const response = await api.put(
        `merchants/${merchantID}/routing-rules/${id}`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response);
    } catch (err) {
      handleApiError(err, error);
    }
  }
  const updateRoutingRuleStatus = async (id, ruleStatus, success, error) => {
    try {
      const merchantID = getUserDetails()?.merchantId;
      const response = await api.put(
        `merchants/${merchantID}/routing-rules/${id}/statuses/${ruleStatus}`,
        {},
        {
          headers: {
            Authorization: getAccessToken(),
          },
        }
      );
      success(response?.data);
    } catch (err) {
      handleApiError(err, error);
    }
  }

  const getRouteRuleData = async (payload, success, error) => {
    try {
      const merchantId = getUserDetails()?.merchantId;
      const response = await api.post(
        `/merchants/${merchantId}/visualizations/entities/merchant_routing_rules`,

        payload,
        {
          headers: {
            Authorization: getAccessToken(),
          },

        },

      );
      success(response.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  const downloadProcessedVisualData = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(
        `/merchants/${merchantID}/visualizations/entities/generic_recon_file_processing_info/download`,
        payload,
        { headers: { Authorization: getAccessToken() } }
      )
      .then((res) => {
        success(res);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };
  const approveRefundRequest = async (transactionId, refundId, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(
        `/merchants/${merchantID}/transactions/${transactionId}/refunds/${refundId}/approvals`,
        {},
        {
          headers: {
            Authorization: getAccessToken(),
            "X-Request-Id": uuidv4(),
          },
        }
      )
      .then((res) => {
        success(res);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };

  const rejectRefundRequest = async (transactionId, refundId, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(
        `/merchants/${merchantID}/transactions/${transactionId}/refunds/${refundId}/declines`,
        payload,
        {
          headers: {
            Authorization: getAccessToken(),
            "X-Request-Id": uuidv4(),
          },
        }
      )
      .then((res) => {
        success(res);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };

  const getSubMerchants = async (payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(
        `/merchants/${merchantID}/visualizations/entities/sub_merchants`,
        payload,
        { headers: { Authorization: getAccessToken() } }
      )
      .then((res) => {
        success(res?.data);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };
  const addSplitPaymentData = async (transactionId, payload, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    await api
      .post(
        `/merchants/${merchantID}/transactions/${transactionId}/split-settlements`,
        payload,
        {
          headers: { Authorization: getAccessToken(), "X-Request-Id": uuidv4() },
        }
      )
      .then((res) => {
        success(res?.data);
      })
      .catch((err) => {
        handleApiError(err, error);
      });
  };

  const deleteRoutingRule = async (id, success, error) => {
    const merchantID = getUserDetails()?.merchantId;
    try {
      const res = await api.delete(`/merchants/${merchantID}/routing-rules/${id}`, {
        headers: {
          Authorization: getAccessToken(),
        },
      });
      success(res.data);
    } catch (err) {
      handleApiError(err, error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        api,
        merchantEmail,
        merchantLogo,
        merchantName,
        isLoggedIn,
        login,
        validateToken,
        initiateMasqueradeSamlLogin,
        merchantSettings,
        signUp,
        getUserDetails,
        getAccessToken,
        getPspConfigSchemas,
        savePspConfigs,
        getPspConfig,
        delPspConfigu,
        getAllPspConfigs,
        updatePaymentMethod,
        getAllPaymentMethods,
        getAllModalData,
        getRoutes,
        getOrderDetails,
        getOrderDetailsTimeline,
        getApiCredentials,
        getApiCredentialsName,
        getPaymentMethod,
        getRefundData,
        getVoidCaptureData,
        getAllUiAnalyticsEvent,
        getAllVisualizationData,
        getAllVisualizationFilterData,
        getGenericReconVisualizationData,
        logout,
        getBusinessUnits,
        getAllTransactionDetails,
        uploadPaymentFormData,
        getQrDetails,
        credentialsCheck,
        verifyOTP,
        updatePassword,
        merchantProfiles,
        updateMerchantProfiles,
        changePassword,
        updateCheckoutSettings,
        getDownloadFile,
        getDownloadFileWithFilters,
        generateReports,
        generateGenericReconReports,
        getAllReportType,
        getAllReportStatus,
        captureRefundPayment,
        getRefundReasons,
        isAuthorizedFeature,
        isAuthorizedPermission,
        defaultPageLink,
        getProductDetails,
        getCountryNames,
        postPspConfigForFiltering,
        addRoutingRule,
        getRoutingRules,
        getRoutingRuleById,
        editRoutingRules,
        deleteRoutingRules,
        getDownloadPSPReport,
        addBusinessUnit,
        updateBusinessUnit,
        addProduct,
        updateProduct,
        deleteProduct,
        getAllWebhookDetails,
        getSaltKey,
        getSpecificIdWebhookDetails,
        createWebhook,
        updateWebhoookDetails,
        deleteWebhookDetails,
        postNotificationStatus,
        getAllUsers,
        createUsers,
        getAllRoles,
        getAllPermissions,
        createRole,
        updateUser,
        updateRole,
        getRoleById,
        deleteUserById,
        deleteRoleById,
        resendInviteForUser,
        updateLogo,
        supportedMethods,
        postNotificationStatus,
        getReconSampleFile,
        getReconConflictFile,
        postReconFile,
        getSupportedPspRecon,
        isSupportedRecon,
        postSettlementFiles,
        handleApiError,
        checkPermission,
        twoFAAuth,
        expirePaymentLink,
        timezone,
        approvalCount,
        setApprovalCount,
        resendSMSAndEmail,
        editPaymentLink,
        getAllOffer,
        getAllSetupConfigs,
        getAllEmiOffers,
        getEmiOfferById,
        deleteEmiOfferById,
        addEmiRule,
        updateEmiRule,
        getGenericReconConfig,
        getGenericReconRecordFiles,
        postGenericReconFile,
        getGenericReconTableData,
        downloadInvalidReconFile,
        revertReconFile,
        refreshOrderDetail,
        globalFileUpload,
        globalFileDownload,
        downloadFileUrlOpen,
        getReconTags,
        getDynamicRoute,
        NavigateToAvailableRoute,
        getProductDetailsById,
        routingRuleSuggester,
        addRoutingRules,
        updateRoutingRule,
        updateRoutingRuleStatus,
        getRouteRuleData,
        deleteRoutingRule,
        downloadProcessedVisualData,
        getFileSetCodesByTags,
        // getGenericReconciliationsConfig,
        getSubMerchants,
        addSplitPaymentData,
        getGenericReconciliationConfig,
        getGenericReconciliationVisualizationData,
        revertReconciliationFile,
        getGenericReconciliationRecordFiles,
        getReconciliationTags,
        approveRefundRequest,
        rejectRefundRequest
      }}
    >
      {children}

      <SessionExpiredModal
        open={isSessionModalOpen}
        setIsSessionModalOpen={setIsSessionModalOpen}
      />

      {isConflictIdentityErrorModalOpen && (
        <div className="sessionModal">
          <ConflictIdentityErrorModal open={true} />
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
